import React, { useCallback } from "react";
import { TableRowActions } from "../../TableRowActions";
import { useTableRowActionContext } from "@/lib/hooks/ui/useTableRowActionContext";
import { DetailActionProvider } from "@/contexts/DetailActionContext";
import type { BaseEntity, SimpleTableConfig, EntityHooks } from "../../types";
import type { DetailAction } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { RowActionRoleFilter } from "../../contexts/SimpleTableRowServicesContext";

/**
 * The row-actions UI with the update/delete mutations and the role filter
 * INJECTED.
 *
 * Split out of `TableRowActionsWrapper` so the two callers differ only in where
 * the mutations come from: standalone surfaces (cards, drilldowns, detail
 * panels — see ENTITY_TABLE_ROW_ACTIONS) keep using `TableRowActionsWrapper`,
 * which subscribes per instance, while rows inside a `SimpleTable` read the ONE
 * table-level subscription out of `SimpleTableRowServicesContext`.
 *
 * `DetailActionProvider` stays HERE, per row, and cannot be hoisted: its value
 * is `rowActionContext`, which is built from THIS row's `item` (entity ids,
 * `onRefresh`, the modal payloads). One provider per table would hand every row
 * the same item.
 */
export interface TableRowActionsBodyProps<TData extends BaseEntity> {
  item: TData;
  actions: DetailAction<TData>[];
  hooks: EntityHooks<TData>;
  entityName: string;
  config?: SimpleTableConfig<TData>;
  compact?: boolean;
  maxVisibleActions?: number;
  /** Optional refetch callback - when provided, used for onRefresh after actions (e.g. embedded tables) */
  refetch?: () => void | Promise<void>;
  updateMutation: ReturnType<EntityHooks<TData>["useUpdate"]>;
  deleteMutation: ReturnType<EntityHooks<TData>["useDelete"]>;
  filterActionsByRole: RowActionRoleFilter;
}

export function TableRowActionsBody<TData extends BaseEntity>({
  item,
  actions,
  hooks,
  entityName,
  config,
  compact = true,
  maxVisibleActions = 2,
  refetch: refetchProp,
  updateMutation,
  deleteMutation,
  filterActionsByRole,
}: TableRowActionsBodyProps<TData>) {
  // Use provided refetch or no-op - for embedded tables (e.g. company detail contacts) pass refetch
  const refetch = useCallback(() => {
    if (refetchProp) {
      return refetchProp();
    }
    return Promise.resolve();
  }, [refetchProp]);

  // Build action context for this row
  // When refetchProp provided (embedded table), use it for onDeleteComplete to stay on page
  const rowActionContext = useTableRowActionContext({
    item,
    hooks,
    refetch,
    updateMutation,
    deleteMutation,
    entityName,
    config,
    onDeleteComplete: refetchProp ?? undefined,
  });

  if (!rowActionContext) {
    return null;
  }

  // Wrap in DetailActionProvider so modals can access the context
  return (
    <DetailActionProvider value={rowActionContext}>
      <TableRowActions
        item={item}
        actions={actions}
        actionContext={rowActionContext}
        maxVisibleActions={maxVisibleActions}
        className="w-full"
        compact={compact}
        filterActionsByRole={filterActionsByRole}
      />
    </DetailActionProvider>
  );
}
